import Component from '@glimmer/component';
import { service } from '@ember/service';
import {
  getConfigForTasField,
  getAllValuesForTasField,
  getSingleValueForTasField,
} from '../../../utils/tuition-assistance/fields.ts';
import { DEFAULT_HUMAN_AUDIT_PCT, isHumanAuditPctUnset } from '../../../utils/tas/audit.ts';
import type IntlService from 'ember-intl/services/intl';
import type { TasProgramTemplateExtension as TasProgramTemplate } from 'tio-data/data/resources/tas-program-template.ext';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/data/resources/tas-program-template.type';
import type { TemplateFieldName } from '../../../utils/tuition-assistance/fields.ts';
import { t } from 'ember-intl';
import ApprovalWorkflowApprovalLayoutReview from './approval-workflow/approval-layout-review.gts';
import TermsAndConditionsTable from './terms-and-conditions-table.gts';
import { isEmpty } from 'tio-ui/utilities';
import DefinitionList from '../../ui/definition-list.gts';
import { Section } from 'tio-ui/components/layout';
import Table from 'tio-ui/components/table';

interface TuitionAssistanceProgramTemplateReviewProgramSetupReviewSignature {
  Args: {
    model: TasProgramTemplate;
  };
}

export default class TuitionAssistanceProgramTemplateReviewProgramSetupReviewComponent extends Component<TuitionAssistanceProgramTemplateReviewProgramSetupReviewSignature> {
  @service declare intl: IntlService;

  get reviewFields() {
    return (this.args.model.fields || {}) as TASProgramTemplateModelFieldsSignature;
  }

  get programSetupFields(): TemplateFieldName[] {
    return [
      'PROGRAM_NAME',
      'PROGRAM_CODE',
      'PROGRAM_DESCRIPTION',
      'PROGRAM_PAYMENT',
      'CALCULATE_TOWARDS_TOTAL_BASED_ON',
      'LOCK_EMPLOYEE_STATUS_BASED_ON',
      'CLOSE_PROGRAM_BASED_ON',
      'SUSPENDED_PROGRAM_DISCLOSURE',
      'DIRECT_PAY_DISCLOSURE',
    ];
  }

  /**
   * Returns whether Course Cash is enabled for this program.
   */
  get directPayProgramTypeField() {
    const value = getSingleValueForTasField('PAYMENT_TYPE_FOR_DIRECT_PAY', this.reviewFields);
    const isCourseCash = value === 'TAS.AssistanceManner.COURSE_CASH';
    return {
      label: 'Course Cash Enabled',
      value: isCourseCash ? this.intl.t('common.yes') : this.intl.t('common.no'),
    };
  }

  get institutionFields() {
    return [
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.allow_unaccredited_institutions'
        ),
        dataKey: 'ALLOW_UNACCREDITED_INSTITUTIONS',
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.institution_list_is_exclusive'
        ),
        dataKey: 'INSTITUTION_LIST_IS_EXCLUSIVE',
      },
    ];
  }

  get programLimitFields() {
    const isTrackingOnlyProgram = this.args.model.isTrackingOnlyProgram;
    return [
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.can_there_be_multiple_instances'
        ),
        dataKey: 'MULTIPLE_INSTANCE_PROGRAM',
        isVisible: true,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.is_this_single_application_program'
        ),
        dataKey: 'SINGLE_APPLICATION_PROGRAM',
        isVisible: true,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.certification_tracking_enabled'
        ),
        dataKey: 'CERTIFICATE_OTHER_PROGRAM',
        isVisible: !isTrackingOnlyProgram,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.allow_pre_approval_submission_beyond_limits'
        ),
        dataKey: 'ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT',
        isVisible: !isTrackingOnlyProgram,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.use_balance_multiplier'
        ),
        dataKey: 'USE_BALANCE_MULTIPLIER_FOR_PROGRAM_LIMITS',
        isVisible: !isTrackingOnlyProgram,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.lock_balance_multiplier'
        ),
        dataKey: 'LOCK_BALANCE_MULTIPLIER_AT_START_OF_FISCAL_YEAR',
        isVisible: !isTrackingOnlyProgram,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.enable_credit_based_limit_capacity'
        ),
        dataKey: 'LIMIT_CAPACITY',
        isVisible: !isTrackingOnlyProgram,
        formatter: (
          value: string | number | boolean | null | undefined | Record<string, unknown>
        ) => this.formatBoolean(value === 'TAS.LimitCapacity.CREDITS'),
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.calculate_reimbursement_by_credit'
        ),
        dataKey: 'PRE_CALC_REQUESTED_TOTAL_BY_CREDIT',
        isVisible: !isTrackingOnlyProgram,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.reimbursement_per_course_credit'
        ),
        dataKey: 'REIMBURSEMENT_PER_COURSE_CREDIT',
        isVisible:
          !isTrackingOnlyProgram &&
          getSingleValueForTasField('PRE_CALC_REQUESTED_TOTAL_BY_CREDIT', this.reviewFields),
        formatter: this.formatMoney,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.calculate_reimbursement_by_percent'
        ),
        dataKey: 'PRE_CALC_REQUESTED_TOTAL_BY_PERCENT',
        isVisible: !isTrackingOnlyProgram,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.reimbursement_percentage'
        ),
        dataKey: 'REIMBURSEMENT_PERCENTAGE',
        isVisible:
          !isTrackingOnlyProgram &&
          getSingleValueForTasField('PRE_CALC_REQUESTED_TOTAL_BY_PERCENT', this.reviewFields),
        formatter: this.formatPercent,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.calculate_reimbursement_by_claim_financials'
        ),
        dataKey: 'CALCULATE_REIMBURSEMENT_BY_CLAIMS_FINANCIALS',
        isVisible: getSingleValueForTasField(
          'CALCULATE_REIMBURSEMENT_BY_CLAIMS_FINANCIALS',
          this.reviewFields
        ),
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.dependent_program.label'
        ),
        dataKey: 'DEPENDENT_PROGRAM',
        isVisible: true,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.voucher_program.label'
        ),
        dataKey: 'VOUCHER_PROGRAM',
        isVisible: true,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.voucher_program.type'
        ),
        dataKey: 'VOUCHER_FILENAME',
        isVisible: getSingleValueForTasField('VOUCHER_PROGRAM', this.reviewFields),
      },
    ];
  }

  get recommendationsEnabled() {
    return getSingleValueForTasField('ENABLE_AGENT_RECOMMENDATIONS', this.reviewFields);
  }

  // Audits are on unless the program opts out (tio-api#10525); the rate below
  // only bites while they are.
  get humanAuditEnabled() {
    return (
      this.recommendationsEnabled &&
      !getSingleValueForTasField('DISABLE_HUMAN_DECISION_AUDITS', this.reviewFields)
    );
  }

  get recommendationFields() {
    return [
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.enable_agent_recommendations'
        ),
        dataKey: 'ENABLE_AGENT_RECOMMENDATIONS' as TemplateFieldName,
        isVisible: true,
        formatter: this.formatBoolean,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.adhere_to_recommendation_pct'
        ),
        dataKey: 'ADHERE_TO_RECOMMENDATION_PCT' as TemplateFieldName,
        isVisible: this.recommendationsEnabled,
        formatter: this.formatPercent,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.analyzer_maximum_iterations'
        ),
        dataKey: 'ANALYZER_MAXIMUM_ITERATIONS' as TemplateFieldName,
        isVisible: this.recommendationsEnabled,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.mark_for_audit_base_pct'
        ),
        dataKey: 'MARK_FOR_AUDIT_BASE_PCT' as TemplateFieldName,
        isVisible: this.recommendationsEnabled,
        formatter: (value: number | null | undefined) => `${value ?? 0}%`,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.audit_amount_threshold'
        ),
        dataKey: 'MARK_FOR_AUDIT_AMOUNT_THRESHOLD_CENTS' as TemplateFieldName,
        isVisible: this.recommendationsEnabled,
        formatter: this.formatMoney,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.audit_high_amount_pct'
        ),
        dataKey: 'MARK_FOR_AUDIT_HIGH_AMOUNT_PCT' as TemplateFieldName,
        isVisible: this.recommendationsEnabled,
        formatter: (value: number | null | undefined) => (value ? `${value}%` : 'None'),
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.audit_human_decisions'
        ),
        dataKey: 'DISABLE_HUMAN_DECISION_AUDITS' as TemplateFieldName,
        isVisible: this.recommendationsEnabled,
        formatter: this.formatAuditsEnabled,
      },
      {
        label: this.intl.t(
          'common.tuition_assistance.program_template_review.mark_for_audit_base_pct_human'
        ),
        dataKey: 'MARK_FOR_AUDIT_BASE_PCT_HUMAN' as TemplateFieldName,
        isVisible: this.humanAuditEnabled,
        formatter: this.formatHumanAuditPct,
      },
    ];
  }

  get cannedApproverResponses(): { [key: string]: TemplateFieldName } {
    return {
      response_template_for_approval: 'WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT',
      response_template_for_rejection: 'WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT',
      response_template_for_revision: 'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT',
    };
  }

  formatPercent = (value: number) => {
    return `${value}%`;
  };

  formatBoolean = (
    value: number | string | boolean | null | undefined | Record<string, unknown>
  ) => {
    return value ? this.intl.t('common.yes') : this.intl.t('common.no');
  };

  // The field stores the opt-out; the row reads as the audit state it produces.
  formatAuditsEnabled = (disabled: boolean | null | undefined) => this.formatBoolean(!disabled);

  // An unset rate is the server default, not "none" — say which it is.
  formatHumanAuditPct = (value: number | null | undefined) =>
    isHumanAuditPctUnset(value)
      ? this.intl.t('common.tuition_assistance.program_template_review.audit_pct_default', {
          pct: DEFAULT_HUMAN_AUDIT_PCT,
        })
      : `${value}%`;

  formatMoney = (cents: number | string | boolean | null | undefined | Record<string, unknown>) => {
    if (!Number(cents)) {
      return 'None';
    }

    const dollars = Number(cents) / 100;

    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  formatLimitValue = (
    value: number | string | boolean | null | undefined | Record<string, unknown>
  ) => {
    if (!Number(value)) {
      return 'None';
    }

    const limitCapacity = getSingleValueForTasField('LIMIT_CAPACITY', this.reviewFields);
    const isCreditBased = limitCapacity === 'TAS.LimitCapacity.CREDITS';

    if (isCreditBased) {
      // Format as integer credits with localization
      const numericValue = Number(value);
      const formattedNumber = this.intl.formatNumber(numericValue);
      const creditsLabel = this.intl.t('common.credits');
      return `${formattedNumber} ${creditsLabel}`;
    }

    // Format as currency
    const dollars = Number(value) / 100;
    return this.intl.formatNumber(dollars, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  <template>
    <Section>
      <:header>
        {{t "common.tuition_assistance.program_template_review.program_setup"}}
      </:header>
      <:body>
        <DefinitionList class="w-full lg:w-1/2" as |options|>
          {{#each this.programSetupFields as |field|}}
            {{#let (getConfigForTasField field this.reviewFields) as |config|}}
              <div class={{options.wrappingClass}}>
                <options.dt>{{config.label}}</options.dt>
                {{! @glint-expect-error: Only primitive values and certain DOM objects are usable as top-level template content}}
                <options.dd>{{config.value}}</options.dd>
              </div>
            {{/let}}
          {{/each}}

          <div class={{options.wrappingClass}}>
            <options.dt>{{this.directPayProgramTypeField.label}}</options.dt>
            <options.dd>{{this.directPayProgramTypeField.value}}</options.dd>
          </div>

          {{#each this.programLimitFields as |field|}}
            {{! @glint-expect-error: Seems like dataKey is not correct type}}
            {{#let (getSingleValueForTasField field.dataKey this.reviewFields) as |value|}}
              {{#if field.isVisible}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{field.label}}</options.dt>
                  {{#if field.formatter}}
                    {{! @glint-expect-error: who knows}}
                    <options.dd>{{field.formatter value}}</options.dd>
                  {{else}}
                    {{! @glint-expect-error: value is type never}}
                    <options.dd>{{value}}</options.dd>
                  {{/if}}
                </div>
              {{/if}}
            {{/let}}
          {{/each}}
        </DefinitionList>

        {{#if (getSingleValueForTasField "DEPENDENT_PROGRAM" this.reviewFields)}}
          {{#let
            (getSingleValueForTasField "DEPENDENT_PROGRAM_AGE_LIMITS" this.reviewFields)
            as |ageLimit|
          }}
            <h3 class="font-semibold uppercase">
              {{t
                "common.tuition_assistance.program_template_review.dependent_program.dependent_age_limits"
              }}
            </h3>
            {{#if (isEmpty ageLimit)}}
              {{t
                "common.tuition_assistance.program_template_review.dependent_program.no_age_limits_were_set"
              }}
            {{else}}
              <DefinitionList as |options|>
                {{#each-in ageLimit as |label value|}}
                  <div class={{options.wrappingClass}}>
                    <options.dt>{{label}}</options.dt>
                    {{! @glint-expect-error: value is type never}}
                    <options.dd>{{value}}</options.dd>
                  </div>
                {{/each-in}}
              </DefinitionList>
            {{/if}}
          {{/let}}
        {{/if}}
      </:body>
    </Section>

    <Section>
      <:header>{{t "common.program_builder.recommendations.default"}}</:header>
      <:body>
        <DefinitionList class="max-w-[50%]" as |options|>
          {{#each this.recommendationFields as |field|}}
            {{#let (getSingleValueForTasField field.dataKey this.reviewFields) as |value|}}
              {{#if field.isVisible}}
                <div class={{options.wrappingClass}}>
                  <options.dt>{{field.label}}</options.dt>
                  {{#if field.formatter}}
                    {{! @glint-expect-error: who knows}}
                    <options.dd>{{field.formatter value}}</options.dd>
                  {{else}}
                    {{! @glint-expect-error: value is type never}}
                    <options.dd>{{value}}</options.dd>
                  {{/if}}
                </div>
              {{/if}}
            {{/let}}
          {{/each}}
        </DefinitionList>
      </:body>
    </Section>

    <Section>
      <:header>{{t "common.institutions"}}</:header>
      <:body>
        <DefinitionList class="max-w-[50%]" as |options|>
          {{#each this.institutionFields as |field|}}
            <div class={{options.wrappingClass}}>
              {{! @glint-expect-error: Seems like dataKey is not correct type}}
              {{#let (getSingleValueForTasField field.dataKey this.reviewFields) as |value|}}
                <options.dt>{{field.label}}</options.dt>
                <options.dd>{{this.formatBoolean value}}</options.dd>
              {{/let}}
            </div>
          {{/each}}
        </DefinitionList>
      </:body>
    </Section>

    {{#unless @model.isTrackingOnlyProgram}}
      <Section>
        <:header>
          {{t "common.tuition_assistance.program_template_review.program_limits.default"}}
        </:header>
        <:body>
          <Table>
            <:tbody as |options|>
              <options.tr>
                <options.td>
                  {{t "common.tuition_assistance.program_template_review.program_limits.lifetime"}}
                </options.td>
                <options.td>
                  {{t
                    "common.tuition_assistance.program_template_review.program_limits.all_employment_types"
                  }}
                </options.td>
                <options.td>
                  {{this.formatLimitValue
                    (getSingleValueForTasField "MAXIMUM_LIFETIME_REIMBURSEMENT" this.reviewFields)
                  }}
                </options.td>
              </options.tr>

              <options.tr>
                <options.td>
                  {{t "common.annual"}}
                </options.td>
                <options.td>
                  {{t
                    "common.tuition_assistance.program_template_review.program_limits.all_employment_types"
                  }}
                </options.td>
                <options.td>
                  {{this.formatLimitValue
                    (getSingleValueForTasField "MAXIMUM_ANNUAL_REIMBURSEMENT" this.reviewFields)
                  }}
                </options.td>
              </options.tr>

              {{#let
                (getSingleValueForTasField
                  "MAXIMUM_LIFETIME_REIMBURSEMENT_BY_EMPLOYMENT_TYPE" this.reviewFields
                )
                as |limit|
              }}
                {{#each-in limit as |label value|}}
                  <options.tr>
                    <options.td>
                      {{t
                        "common.tuition_assistance.program_template_review.program_limits.lifetime"
                      }}
                    </options.td>
                    <options.td>{{label}}</options.td>
                    {{! @glint-expect-error: label is type never}}
                    <options.td>{{this.formatLimitValue value}}</options.td>
                  </options.tr>
                {{/each-in}}
              {{/let}}

              {{#let
                (getSingleValueForTasField
                  "MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE" this.reviewFields
                )
                as |limit|
              }}
                {{#each-in limit as |label value|}}
                  <options.tr>
                    <options.td>{{t "common.annual"}}</options.td>
                    <options.td>{{label}}</options.td>
                    {{! @glint-expect-error: label is type never}}
                    <options.td>{{this.formatLimitValue value}}</options.td>
                  </options.tr>
                {{/each-in}}
              {{/let}}
            </:tbody>
          </Table>
        </:body>
      </Section>
    {{/unless}}

    <ApprovalWorkflowApprovalLayoutReview
      @workflowStepName={{t
        "common.tuition_assistance.program_template_review.withdraw_mark_complete"
      }}
      @reviewFields={{this.reviewFields}}
      @workflowStepType={{t "common.tuition_assistance.program_template_review.overall"}}
      @approvalWorkflowName="PROGRAM_APPROVE_WITHDRAW_COMPLETE_WORKFLOW"
      {{! @glint-expect-error: Type 'FieldValue[]' is not assignable to type 'TemplateApprovalWorkflowApprover[]'.}}
      @approverList={{getAllValuesForTasField
        "PROGRAM_APPROVE_WITHDRAW_COMPLETE_WORKFLOW"
        this.reviewFields
      }}
      @cannedApproverResponses={{this.cannedApproverResponses}}
    />

    <Section>
      <:header>{{t "common.terms_and_conditions"}}</:header>
      <:body>
        <TermsAndConditionsTable @conditions={{@model.programApproveWithdrawCompleteConditions}} />
      </:body>
    </Section>
  </template>
}
